/* ═══════════════════════════════════════════════════════════════════════════
   HexClean — domain model
   ───────────────────────────────────────────────────────────────────────────
   This file is the contract the Go backend will implement. Every field name
   here is the JSON field name; nothing is renamed at the boundary, so a
   handler can be written against this file directly and the client needs no
   mapping layer.

   Money is an integer count of the currency's minor unit — `halalas` for SAR,
   of which there are 100 to the riyal. Floats are never used for money:
   0.1 + 0.2 is a rounding bug waiting for a cart with three items in it. How
   many minor units make a major one is a property of the market and lives on
   the country definition in lib/geo.ts, never inline.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Minor units. SAR 25.00 is `2500`. Always integer. */
export type Money = number

export type LocalizedText = { en: string; ar: string }

/* ── catalog ──────────────────────────────────────────────────────────────── */

/** A category's stable key, as the server minted it.
 *
 *  A bare `string`, and it has to be. This was a union of the nine ids the seed
 *  catalogue ships with — `laundry | cleaning | carwash | …` — which is a
 *  compile-time claim that those nine are the only categories that can ever
 *  exist. The live API publishes `water-delivery`, `home-cleaning` and
 *  `car-wash`, so **not one** real category inhabited the type.
 *
 *  What that bought was not safety. It bought eight `as CategoryId` casts at
 *  the points where real data enters — four in the HTTP adapter, two on the
 *  booking screen, one in the mock, one in the seed — each of them the compiler
 *  being told to stop asking a question it was right to ask. A type every live
 *  value has to be cast into is not checking anything; it is a lie with
 *  ceremony around it.
 *
 *  Categories are operator-managed — `domain.Category` in Go is explicit that
 *  the set is CRUD from the admin panel "rather than a constant compiled into
 *  three front ends" — so an id minted this afternoon is valid and no union
 *  written today can contain it. The type that describes that honestly is
 *  `string`, and the checking that actually matters happens where the data
 *  does: against `GET /categories`.
 *
 *  Named rather than inlined so the intent survives — a field typed
 *  `CategoryId` still says what the string is for, and `CATEGORIES` in
 *  `lib/seed/catalog.ts` is where the nine demo ids live.
 *
 *  `hexclean-partner` and `hexclean-admin` both carry this same widening. */
export type CategoryId = string

/** Which of the eight validated series hues a category paints itself with.
 *  An index into the ramp, never a literal colour, so a theme change or a
 *  re-validated palette moves every tile at once. */
export type SeriesSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

/** Who travels.
 *
 *  `we-come` is the default for every category: someone arrives at the
 *  customer's address. `you-come` exists only where a partner has premises
 *  worth driving to — today that is a car wash and nothing else. The
 *  distinction is not cosmetic: it decides whether the customer is asked to
 *  choose a shop at all, whose price list applies, and which address the
 *  booking is against. */
export type Venue = 'we-come' | 'you-come'

/** How a category's work gets done, which is what the tracking timeline
 *  reports. `visit` is someone arriving and working; `collection` is the
 *  two-trip laundry shape; `dropoff` is goods turning up with no work done on
 *  site. The fourth shape — the customer driving to the partner — is not here
 *  because it is a consequence of the venue rather than of the category. */
export type FulfilmentTrack = 'visit' | 'collection' | 'dropoff'

export interface ServiceCategory {
  id: CategoryId
  name: LocalizedText
  tagline: LocalizedText
  /** lucide-react icon name, resolved through components/Icon so an unknown
   *  name degrades to a placeholder instead of crashing the grid. */
  icon: string
  slot: SeriesSlot
  /** Key into the photo manifest (src/lib/photos.generated.ts). The icon is
   *  kept alongside it rather than replaced: the photo carries the category on
   *  a tile, the icon carries it in a 20px row where a photo would be mud. */
  photo?: string
  /** Ordering flow this category uses. Laundry is a per-piece basket; cleaning
   *  is an appointment with a duration. They are genuinely different funnels
   *  and the checkout branches on this, not on the category id. */
  flow: 'basket' | 'appointment'
  /** How the work is fulfilled, which decides the milestones an order reports.
   *  A separate question from `flow`: water delivery is a `basket` to order and
   *  a `dropoff` to fulfil. See lib/track.ts for why they are not one field. */
  track: FulfilmentTrack
  /** Venues this category can be fulfilled at, in the order they are offered.
   *  A single entry means the question is never put to the customer — which is
   *  every category except car wash. */
  venues: Venue[]
  /** Minimum order on HexClean's own price list, in minor units. */
  minOrder: Money
  /** Call-out fee on HexClean's own price list. Charged per order.
   *
   *  Named for what the API calls it. This was `serviceFee` here and
   *  `deliveryFee` on the wire, which is not a cosmetic disagreement: the field
   *  simply arrived `undefined` in `http` mode, and `subtotal + undefined` is
   *  `NaN`, so the basket total was not a wrong number but not a number at all.
   *  Nothing caught it because the seed catalogue defined `serviceFee`, so mock
   *  mode was correct and every test passed. */
  deliveryFee: Money
  /** Subtotal above which the fee is waived. **Zero means never**, which is the
   *  server's spelling and therefore this one — an optional field would invite
   *  a `?? Infinity` somewhere and quietly disable the waiver. */
  freeDeliveryOver: Money
  /** Platform service hours, 24h local. Slots are generated against these
   *  whenever no partner has been chosen yet, which is the normal case. */
  openFrom: string
  openTo: string
  providerCount: number
}

/** A badge on a partner tile.
 *
 *  Seven, and they divide by **who may set them**. `verified` and `insured`
 *  are the platform's: the service sets them from what the paperwork says, and
 *  an operator ticking "insured" on a partner with no certificate would be the
 *  badge lying. The other five are merchandising an operator sets from the
 *  panel.
 *
 *  This union was the five for months while the API seeded the two, because
 *  `badgeLabel` degrades an unknown id to the id itself — so `verified`
 *  rendered as a Latin word inside an Arabic interface rather than throwing,
 *  and nothing pointed at it. The generated wire model is what finally did:
 *  `Wire.Provider.badges` is `string[]`, and assigning that to a closed union
 *  of five stops compiling. */
export interface Badge {
  id: 'verified' | 'insured' | 'top-rated' | 'fast' | 'eco' | 'new' | 'express'
  label: LocalizedText
}

/** What a partner is, which is the thing that actually differs between them.
 *
 *  A `freelancer` is one person with their own hands — a cleaner, a handyman,
 *  a groomer. A `mobile` operator brings equipment to the customer: the man
 *  with the pressure washer and a van full of water is the archetype. A
 *  `company` is a registered business, and is the only shape that can hold
 *  premises a customer drives to.
 *
 *  They are onboarded against different paperwork, paid on different terms and
 *  insured differently, which is why this is a field rather than a guess made
 *  from the provider's name. */
export type PartnerKind = 'freelancer' | 'mobile' | 'company'

/** The premises behind a `you-come` booking. Not an `Address`: nobody is
 *  navigating a courier here, the customer is driving to a place they can see
 *  from the road, so a landmark earns its place and a floor number does not. */
export interface ProviderSite {
  /** District key from lib/geo.ts, same namespace as `Address.area`. */
  area: string
  /** Localized, like the landmark beside it. A Riyadh street sign carries both
   *  scripts, and "Anas Ibn Malik Road" sitting inside an otherwise-Arabic
   *  address line is the transliteration a driver has to read back into the
   *  name they actually know. */
  street: LocalizedText
  landmark: LocalizedText
  lat: number
  lng: number
}

/* Optional here means "the API does not publish this". Eight of these fields
   exist only in the seed, which is the server in mock mode and nowhere else —
   so against the real API they are absent, and a required field that is absent
   is `undefined` reaching a formatter. That was literally "Min. SARNaN" on
   every provider card. Marking them optional is what makes the compiler ask
   each screen what it wants to do about the gap; `http.ts#fromWireProvider`
   documents which are derived, which are renamed and which are simply not
   there. `minOrder` and `deliveryFee` are the ones to be careful with: they are
   category terms rather than a partner's, per Zain's call on 13 Aug, and
   `termsOf` already reads them off the category. */
export interface Provider {
  id: string
  slug: string
  name: LocalizedText
  categoryId: CategoryId
  kind?: PartnerKind
  /** Venues this partner can serve. A mobile car washer is `['we-come']`; a
   *  car wash with a forecourt is `['you-come']`; a company that does both is
   *  both. Filtered against the venue the customer chose. */
  venues: Venue[]
  /** Present exactly when `venues` includes `you-come`. */
  site?: ProviderSite
  /** Two-letter mark drawn on a series-hue tile. Cheaper than a logo file,
   *  never 404s, and legible at 32px — which a real logo usually is not. It
   *  survives the arrival of `photo` because it is still what sits on the card
   *  as an avatar over the cover, and the fallback when a cover is missing. */
  initials: string
  /** Key into the photo manifest (src/lib/photos.generated.ts). */
  photo?: string
  slot: SeriesSlot
  rating: number
  reviewCount: number
  /** Minutes, as a range. `null` for appointment providers, which quote a
   *  visit window instead of a turnaround. */
  etaMinutes: [number, number] | null
  /** Category terms, not a partner's — read them off the category. Present in
   *  seed data only; see the note above the interface. */
  minOrder?: Money
  deliveryFee?: Money
  /** 1–3, rendered as repeated currency glyphs. */
  priceLevel?: 1 | 2 | 3
  badges: Badge['id'][]
  areas: string[]
  about: LocalizedText
  /** Local time, 24h, in the operating city's zone. */
  openFrom?: string
  openTo?: string
  promo: LocalizedText | null
}

export interface CatalogSection {
  id: string
  name: LocalizedText
}

/** Which price list a row belongs to.
 *
 *  A `CategoryId` means HexClean's own list — the price a customer pays when
 *  we choose the partner, which is the normal case. A provider id means that
 *  partner's own list, which is only reachable where the customer chose the
 *  partner themselves. Today the second case exists solely for a car wash you
 *  drive to: the forecourt sets its own prices because the customer can read
 *  them off the board when they arrive.
 *
 *  Both are plain string ids and they share a namespace by construction —
 *  provider ids are prefixed, category ids are not — so one lookup serves
 *  both and no caller has to branch on which kind it holds. */
export type ListId = string

export interface CatalogItem {
  id: string
  listId: ListId
  sectionId: string
  name: LocalizedText
  description: LocalizedText
  price: Money
  /** What one unit of this line is. Drives the stepper's suffix and the
   *  receipt wording: "3 pieces" vs "3 hours" vs "3 visits". */
  unit: 'piece' | 'kg' | 'hour' | 'visit' | 'sqm' | 'item'
  popular: boolean
  /** Optional add-ons, e.g. express turnaround, extra scent, eco detergent. */
  options?: ItemOption[]
}

export interface ItemOption {
  id: string
  name: LocalizedText
  priceDelta: Money
}

export interface Review {
  id: string
  providerId: string
  /** Localized: a reviewer signs their name in their own script, and a wall of
   *  Latin initials under Arabic quotes reads as reviews written by somebody
   *  else's customers. */
  author: LocalizedText
  rating: number
  body: LocalizedText
  /** ISO 8601. */
  createdAt: string
}

/* ── customer ─────────────────────────────────────────────────────────────── */

export interface User {
  id: string
  name: string
  phone: string
  email?: string
  /** Minor units of store credit. */
  walletBalance: Money
  loyaltyPoints: number
  referralCode: string
  createdAt: string
}

/** What a completed sign-in returns.
 *
 *  The token exists even though the mock has no use for one. A `User` alone is
 *  proof of nothing to a real server, so if sign-in did not hand back a
 *  credential there would be nowhere for the HTTP client to get an
 *  `Authorization` header — and adding one later means changing the shape of
 *  the sign-in response, which is the one part of an auth flow that is
 *  genuinely expensive to change once clients are in the wild.
 *
 *  `expiresAt` is carried so the client can treat an expired session as signed
 *  out before making a request that is certain to 401. */
export interface AuthSession {
  user: User
  /** Bearer credential. Opaque to the client — never parsed, only forwarded. */
  token: string
  /** ISO 8601. */
  expiresAt: string
}

/** What a sign-in answers. At most one side is populated.
 *
 *  `linkRequired` is not a failure. A Google or password sign-in proves a
 *  provider account and nothing about a number, and accounts here are keyed by
 *  number — so the API answers `200` with this and no session until the holder
 *  proves one. Code that branches on the HTTP status alone sails past it into a
 *  signed-out screen with no error to show. */
export interface SignInResult {
  session?: AuthSession
  linkRequired?: LinkChallenge
}

/** The API asking which number this account is reachable on.
 *
 *  Verification is the mailbox: the provider proved an address and the API
 *  minted nothing, because an account here is still keyed by the number a job
 *  is dispatched to. That number is now *declared* rather than proved — there
 *  is no SMS challenge anywhere in the product — so this step is one text field
 *  and one submit, and it happens exactly once per person.
 *
 *  Not an error. The sign-in was valid; it simply does not name an account
 *  yet, and treating it as a failure would sign somebody out of a provider
 *  session they had just legitimately established. */
export interface LinkChallenge {
  /** The address the provider reported, so the screen can name the account it
   *  is asking about rather than asking into the void. */
  email: string
  /** The provider token this challenge is about, handed straight back with the
   *  number. It never came from the server — it came *from* this client, and it
   *  is kept only because the second leg has to present the same identity the
   *  first one did.
   *
   *  It can go stale: the API refuses a sign-in completed more than a few
   *  minutes ago, deliberately, because an id token the SDK silently renews for
   *  an hour would otherwise be a permanent key. That used to be a state of its
   *  own, because the old flow put an SMS round trip in the middle and could
   *  honestly run past the window. Typing ten digits cannot, so the recovery is
   *  now the ordinary one: sign in again, which for Google is a single tap. */
  pending: string
}


/** Saudi National Address: building number, street, district, city. Modelling
 *  it as a free-text line would make the courier's job impossible, so the
 *  structure is explicit and the three fields the driver actually navigates by
 *  are required.
 *
 *  `area` holds a district *key* from lib/geo.ts, not a display name — it is
 *  matched against `Provider.areas` and must not change when the user switches
 *  language. */
export interface Address {
  id: string
  label: 'home' | 'work' | 'other'
  nickname: string
  area: string
  street: string
  building: string
  /** The four-digit supplement in the Saudi National Address. Genuinely
   *  optional: building + street + district already locates the door. */
  additionalNo?: string
  floor?: string
  apartment?: string
  /** Free text for the last fifty metres — "beige villa, black gate". */
  directions?: string
  lat: number
  lng: number
  isDefault: boolean
}

/* ── ordering ─────────────────────────────────────────────────────────────── */

export interface CartLine {
  /** Stable per (item + chosen options), so two lines of the same item with
   *  different options can coexist and be stepped independently. */
  key: string
  itemId: string
  /** The list this line was priced against. Carried on the line rather than
   *  inferred later so a stored order still explains its own totals after the
   *  price list behind it has moved. */
  listId: ListId
  qty: number
  optionIds: string[]
  notes?: string
}

export interface TimeSlot {
  /** ISO date, `YYYY-MM-DD`. */
  date: string
  /** 24h `HH:mm`. */
  from: string
  to: string
  available: boolean
  /** Peak-hour uplift in minor units, added per order not per item. */
  surcharge: Money
}

/** `mada` is Saudi Arabia's domestic debit network and the rail most cards in
 *  a wallet here actually run on, so it leads the list rather than sitting
 *  under a generic "card". */
export type PaymentMethod = 'card' | 'mada' | 'cash' | 'wallet' | 'apple-pay'

/** Every rung that exists anywhere. No single order sees all of them — which
 *  ones apply is a property of how the work is fulfilled, and lives in
 *  lib/track.ts. `cancelled` is deliberately not on any ladder: it is a
 *  terminal branch off one, not a rung on it, and a timeline that renders it
 *  inline implies the order continued afterwards. */
export type OrderStatus =
  | 'placed'
  | 'confirmed'
  /** Partner is travelling to the customer. Appointments only. */
  | 'on-the-way'
  /** Someone is at the door — or, on a drive-in booking, the customer is at
   *  the forecourt. */
  | 'arrived'
  /** The customer's things have left with the partner. Laundry only. */
  | 'picked-up'
  | 'in-progress'
  /** Something is being brought to the customer. */
  | 'out-for-delivery'
  /** Terminal. Labelled "Completed" on a job somebody performed and
   *  "Delivered" on one where goods arrived — see lib/track.ts. */
  | 'delivered'
  | 'cancelled'
  /** Terminal, and **not** a rung on the ladder — nobody was available. The
   *  offer loop exhausts its attempts and the service moves the order here
   *  rather than leaving it on `placed`, because "we are still looking" and
   *  "nobody is coming" render identically otherwise, and the second one is
   *  what a customer waits all afternoon for. Server-side since the offer loop
   *  existed (`domain.StatusUnfulfilled`); this app had no arm for it, so a
   *  real unfulfilled order rendered the raw key `order.step.unfulfilled` in a
   *  blue "in progress" pill. */
  | 'unfulfilled'

export interface OrderEvent {
  status: OrderStatus
  at: string
  note?: LocalizedText
}

export interface OrderTotals {
  subtotal: Money
  deliveryFee: Money
  surcharge: Money
  discount: Money
  /** Granted credit spent on this order — a signup bonus, a referral reward,
   *  operator goodwill. Capped, together with `discount`, at the commission
   *  HexClean earns on the job: under cash the partner collects at the door, so
   *  anything we give the customer comes out of our fee and can never exceed
   *  it. See `creditCeiling` on the quote. */
  walletApplied: Money
  /** The customer's own refunded money, spent back. Not capped by the ceiling —
   *  it was never a promotion, it is a refund on its way home, and the partner
   *  is made whole for it on their statement rather than out of our fee. Shown
   *  as one "wallet" line with `walletApplied` to the customer; the two are
   *  separate here because only one of them has a ceiling to explain. */
  refundApplied: Money
  /** The VAT contained *within* the total, not added to it — displayed prices
   *  are inclusive, so this is the line that reads "includes VAT of SAR 5.85".
   *  Computed on what was sold rather than on what the card is charged: wallet
   *  credit reduces the payment, not the sale. */
  vat: Money
  total: Money
}

export interface Order {
  id: string
  /** Human-facing reference. Short, unambiguous, no ambiguous glyphs. */
  ref: string
  /** Null until a partner is assigned, which for most orders happens after the
   *  order is placed rather than before it. `placed` therefore means "we are
   *  finding you someone" and `confirmed` means "this person accepted" — the
   *  two rungs the ladder already had, now carrying the weight they should
   *  always have carried. A customer who drove to a car wash has this set from
   *  the start, because they picked the forecourt themselves. */
  providerId: string | null
  categoryId: CategoryId
  venue: Venue
  lines: CartLine[]
  /** The customer's address. Null for a `you-come` booking — the customer is
   *  driving to the partner's site and their own address is irrelevant to it. */
  addressId: string | null
  pickup: TimeSlot | null
  delivery: TimeSlot
  payment: PaymentMethod
  promoCode: string | null
  totals: OrderTotals
  status: OrderStatus
  events: OrderEvent[]
  createdAt: string
  /** When a partner was attached. Absent while still matching. */
  assignedAt?: string
  rating: number | null
}

export interface Promo {
  code: string
  kind: 'percent' | 'flat' | 'free-delivery'
  /** Percent as whole number (15 = 15%), or minor units for `flat`. Ignored
   *  for `free-delivery`. */
  value: number
  minSpend: Money
  /** Ceiling on a percent discount, in minor units. */
  cap: Money | null
  description: LocalizedText
  categoryId: CategoryId | null
}

/* ── joining as a partner ─────────────────────────────────────────────────────
   Supply is the side of a marketplace that is actually hard to get, so the
   application is a first-class object rather than a contact form that lands in
   an inbox. It is one model for all three shapes of partner: the fields that
   do not apply to a shape are simply absent, and `requiredFields` below is the
   single place that says which those are — the form renders from it and the
   API validates against it, so a field can never be asked for and then ignored,
   or required and never shown. */

/** Where an application has got to.
 *
 *  An application is a *request*, so it ends when somebody decides it:
 *  `approved`, `rejected` and `withdrawn` are terminal and nothing moves off
 *  them. What the partner does for the next three years is the business of
 *  `PartnerAccount.status`, not of a form they filled in once — an operator
 *  suspending a laundry in 2029 should not be editing a 2026 application.
 *
 *  `docs-requested` is the state that makes the rest of it work. Without it,
 *  "we asked for your insurance certificate and are waiting" is
 *  indistinguishable from "nobody has looked at this", both to the applicant
 *  and to the next
 *  operator who opens the queue. It returns to `reviewing` when the file lands.
 *
 *  lib/partner.ts owns which moves are legal, and projects this plus the
 *  account status onto the single intake pipeline the admin board is built
 *  from. */
export type ApplicationStatus =
  | 'received'
  | 'reviewing'
  | 'docs-requested'
  | 'approved'
  | 'rejected'
  | 'withdrawn'

/** Why an application ended, and whether the door is closed.
 *
 *  Carried because silence is the reason good supply goes to the competitor,
 *  and because "no" and "not yet" are different answers: a cleaner rejected
 *  for an expired Iqama should be told to come back with a new one, and a
 *  cleaner rejected for what happened on their last platform should not. */
export interface ApplicationDecision {
  at: string
  /** Operator id. An approval is somebody's name, not the system's. */
  by: string
  /** Shown to the applicant, so it is written to be read by them. */
  reason: LocalizedText
  mayReapply: boolean
}

export interface PartnerApplication {
  id: string
  /** Human-facing reference, same alphabet as an order's. An applicant who
   *  phones up has something to quote that is not their own phone number. */
  ref: string
  kind: PartnerKind
  /** What they want to be listed under. More than one is allowed — a handyman
   *  who also services air conditioning is one person, not two applications. */
  categoryIds: CategoryId[]
  fullName: string
  phone: string
  email?: string
  /** Districts they will work in. Empty means "wherever you send me", which is
   *  a legitimate answer from a freelancer with a car. */
  areas: string[]
  /** `company` only. */
  businessName?: string
  /** Saudi Commercial Registration number. `company` only, and the one field
   *  that genuinely gates approval. */
  crNumber?: string
  /** Whether the business is registered for VAT. `company` only, and asked
   *  rather than assumed: below the registration threshold there is genuinely
   *  no certificate to send, and demanding one from a two-van firm is how a
   *  real business decides the form is broken. It also decides whose VAT
   *  number goes on the customer's invoice, which has a legal answer. */
  vatRegistered?: boolean
  /** `company` only, and only where the premises are the point: the forecourt
   *  a customer would drive to. */
  siteAddress?: string
  /** `freelancer` and `mobile`. Whole years. */
  yearsExperience?: number
  /** Whether they arrive with their own kit — a mobile car washer with no tank
   *  is a different proposition from one with a van. */
  hasEquipment?: boolean
  hasTransport?: boolean
  note?: string
  status: ApplicationStatus
  createdAt: string
  /** Set when the decision is made. Absent while the application is open. */
  decision?: ApplicationDecision
  /** The account approval created. The join between an application and a
   *  partner that exists — without it, an approved application is a note
   *  saying somebody said yes once. */
  partnerId?: string
}

/** The parts of an application an applicant actually fills in. The rest —
 *  id, reference, status, timestamp — are ours to assign. */
export type ApplicationField = Exclude<
  keyof PartnerApplication,
  'id' | 'ref' | 'status' | 'createdAt'
>

/** The fields each partner shape must supply. The join form builds itself from
 *  this and `submitApplication` validates against it, so the two cannot drift. */
/** `email` is on all three since the SMS went. It is what an approved partner
 *  signs in with — verification is the mailbox — so an application without one
 *  approves into an account nobody can enter, and the number on it can never be
 *  safely claimed by its owner. `PartnerApplication.Validate` refuses an empty
 *  one, which is what makes leaving it off this table a form that submits into
 *  a 400. */
export const APPLICATION_REQUIRED: Record<PartnerKind, readonly ApplicationField[]> = {
  freelancer: ['fullName', 'phone', 'email', 'categoryIds', 'yearsExperience'],
  mobile: ['fullName', 'phone', 'email', 'categoryIds', 'hasEquipment', 'hasTransport'],
  company: ['fullName', 'phone', 'email', 'categoryIds', 'businessName', 'crNumber'],
}

/* ── documents ────────────────────────────────────────────────────────────────
   A form with a name and a phone number on it is a lead, not an application.
   Nobody is sent to a customer's home on the strength of one, so the file
   below is the evidence behind every claim the form made. */

export type DocumentKind =
  /** Identity of the person who will be at the door. Everyone, and — for a
   *  company — everyone they send, not only the owner who signed up. */
  | 'national-id'
  /** Commercial Registration certificate. The number on the form is a claim;
   *  this is the evidence. Company only. */
  | 'cr'
  /** Company, where registered. Decides whose VAT number goes on the invoice,
   *  which is a question with a legal answer rather than a preference. */
  | 'vat'
  /** IBAN letter from the bank. There is otherwise nowhere to send money, and
   *  an IBAN typed into a form is a payment to a stranger waiting to happen. */
  | 'bank-letter'
  /** Somebody has to cover a flooded flat. Company and mobile. */
  | 'insurance'
  /** Vehicle registration. Mobile — the van is the business. */
  | 'vehicle'

/** `requested` is a document we have asked for and not received; a document
 *  nobody has asked for yet has no record at all. The distinction is what lets
 *  an applicant see a to-do list instead of a rejection they cannot act on. */
export type DocumentStatus = 'requested' | 'submitted' | 'verified' | 'rejected' | 'expired'

export interface PartnerDocument {
  id: string
  applicationId: string
  /** Set once approval has created the account, so a renewal three years later
   *  is still findable from the partner rather than only from the application. */
  partnerId?: string
  /** Set where the document belongs to a person rather than to the business —
   *  a driver's Iqama, not the firm's CR. */
  staffId?: string
  kind: DocumentKind
  status: DocumentStatus
  /** Key into private object storage. Never a URL: these are ID scans, and a
   *  field typed as a URL is a field that ends up in an `<img src>`. */
  fileKey?: string
  /** ISO date, on the documents that expire. Re-requested before they lapse
   *  rather than after, because after means a partner is stood down mid-week. */
  expiresAt?: string
  /** Why it was rejected, or what specifically was asked for. */
  note?: string
  requestedAt: string
  submittedAt?: string
  reviewedAt?: string
}

/* ── the partner account, and the people inside it ────────────────────────────
   A partner is not a login. A laundry with eight staff and three drivers
   cannot be one phone, and modelling it as one is how the app ends up with a
   shared password written on the wall behind the counter.

   So: an account is the business we contracted with and pay, and a staff
   member is a person who holds roles inside it. An offer goes to the account;
   for a company the dispatcher accepts it and assigns a worker. A freelancer
   holds all three roles and is never shown the distinction — the model is only
   allowed to cost the one-person case if it costs them nothing. */

export type StaffRole =
  /** Signed the contract. Money, staff and bank details are theirs alone. */
  | 'owner'
  /** Takes the offers and decides who goes. The role that exists because a
   *  company's phone is not the same phone as its van's. */
  | 'dispatcher'
  /** Does the job. Sees the address of the job they are on and nothing else —
   *  a customer's home address is not a directory for eight employees. */
  | 'worker'

/** `invited` is a person the owner has added who has not signed in yet. They
 *  hold roles already, so a dispatcher can plan around a driver who starts on
 *  Sunday, and they cannot be assigned work until they are `active`. */
export type StaffStatus = 'invited' | 'active' | 'suspended'

export interface StaffMember {
  id: string
  partnerId: string
  name: string
  phone: string
  /** More than one, and usually is. A small firm's owner also drives. */
  roles: StaffRole[]
  status: StaffStatus
  /** Deliberately not `Locale`. Much of this workforce works in Urdu, Hindi,
   *  Bengali or Tagalog, and a union of two is how an app ends up shouting
   *  instructions in a language the person holding the phone does not read.
   *  BCP-47 tags; the customer app stays `'en' | 'ar'` and this does not. */
  languages: string[]
  /** Districts this person covers, where it is narrower than the account's.
   *  Empty means "wherever the account works". */
  areas: string[]
  createdAt: string
}

/** Where a partner is, once they are a partner.
 *
 *  `paused` is theirs and `suspended` is ours, and they are two states rather
 *  than one flag because the difference is the entire message: a partner who
 *  took a week off should not read that they have been suspended, and one we
 *  stood down for an expired insurance certificate should not think they did
 *  it to themselves. */
export type PartnerStatus = 'onboarding' | 'active' | 'paused' | 'suspended'

export interface PartnerAccount {
  id: string
  /** The application this grew out of. Approval is a conversion, not a
   *  re-typing: an approved partner who has to be entered by hand is how one
   *  ends up live in the wrong district. */
  applicationId: string
  kind: PartnerKind
  /** Trading name for a company, the person's own name for the other two. */
  name: LocalizedText
  phone: string
  email?: string
  status: PartnerStatus
  /** Why they are paused or suspended. Shown to them, not only to us. */
  statusNote?: string
  /** The listings this account owns — one per category it was approved for.
   *  A handyman who also services air conditioning is one account and two
   *  provider rows, because the customer browses by category. */
  providerIds: string[]
  categoryIds: CategoryId[]
  areas: string[]
  /** Basis points of the customer's pre-VAT total: 1250 is 12.5%. Basis points
   *  rather than a float because a commission rate is money's cousin and
   *  12.5% stored as 0.125 is the same rounding argument as prices in riyals.
   *  Null means the category default applies, which is the normal case — an
   *  override is a negotiation and should look like one in the data. */
  commissionBps: number | null
  crNumber?: string
  vatNumber?: string
  /** Last four digits only. The full IBAN lives with the payout service; a
   *  bank account number on an admin list view is an exfiltration waiting for
   *  a screenshot. */
  ibanLast4?: string
  createdAt: string
  activatedAt?: string
}

/** What the operator has to supply that the application could not.
 *
 *  An application carries a name, some categories, districts and a kind. A
 *  listing needs a slug, initials, a series slot, hours, a minimum order and a
 *  price list. This is the gap, and it is filled once per category the partner
 *  was approved for. */
export interface ProviderDraft {
  categoryId: CategoryId
  /** Defaults to a slug derived from the trading name, de-duplicated against
   *  the listings that already exist. */
  slug?: string
  name?: LocalizedText
  /** Defaults to `['we-come']`. A `you-come` listing requires premises, and
   *  premises require a company — the two are checked together. */
  venues?: Venue[]
  site?: ProviderSite
  /** Defaults to the districts on the application. */
  areas?: string[]
  priceLevel?: 1 | 2 | 3
  minOrder?: Money
  deliveryFee?: Money
  openFrom?: string
  openTo?: string
  slot?: SeriesSlot
  etaMinutes?: [number, number] | null
  about?: LocalizedText
}

/* ── API envelopes ────────────────────────────────────────────────────────── */

export interface Page<T> {
  items: T[]
  total: number
  cursor: string | null
}

/** Every failure the UI can render differently. A bare `Error` forces the
 *  components to string-match a message, which breaks the moment the copy is
 *  translated. */
export interface ApiError {
  code:
    | 'not-found'
    | 'unauthorized'
    | 'validation'
    | 'conflict'
    | 'offline'
    | 'rate-limited'
    | 'server'
  message: string
  field?: string
}

export class HexApiError extends Error implements ApiError {
  code: ApiError['code']
  field?: string
  constructor(code: ApiError['code'], message: string, field?: string) {
    super(message)
    this.name = 'HexApiError'
    this.code = code
    if (field !== undefined) this.field = field
  }
}

export type SortKey = 'recommended' | 'rating' | 'eta' | 'price-low' | 'price-high'

export interface ProviderFilters {
  categoryId?: CategoryId
  /** Only partners that can serve this venue. The car-wash site list is this
   *  filter and nothing else. */
  venue?: Venue
  area?: string
  q?: string
  sort?: SortKey
  badges?: Badge['id'][]
  minRating?: number
}
